import { Link } from 'react-router-dom';

import { DocPage } from '@/components/doc-page';

export default function PrivacyPage() {
  return (
    <DocPage
      kicker="Privacy policy"
      title="Privacy Policy"
      lead="Bramble has no user accounts and no central database. This page details every piece of data processed by our infrastructure."
      updated="21 July 2026"
    >
      <h2>Summary</h2>
      <ul>
        <li>
          <strong>No accounts.</strong> No sign-up, email, password, or username. We cannot identify
          you.
        </li>
        <li>
          <strong>No tracking.</strong> The app contains no analytics, ads, or third-party tracking
          SDKs.
        </li>
        <li>
          <strong>Local and encrypted.</strong> Names, birth dates, feeds, diapers, sleep, potty
          trips, growth, illnesses and symptoms, medicines and dosage schedules, milestones, and
          notes stay on your iPhone and are encrypted before network sync.
        </li>
        <li>
          <strong>Minimal relay storage.</strong> The relay temporarily stores undelivered encrypted
          messages (deleted upon receipt or after <strong>90 days</strong>) and an Apple push
          notification token.
        </li>
      </ul>

      <h2>What lives on your iPhone</h2>
      <p>
        Each child is stored as a document in Bramble&rsquo;s app container on your device.
        It holds the child&rsquo;s name, birth date, and logged events. Bramble operates locally
        and functions without a network connection. Device backups include this file according to
        Apple&rsquo;s backup settings.
      </p>
      <p>Deleting the app deletes the document. We store no cloud copies.</p>

      <h2>What happens when you sync</h2>
      <p>
        When you invite a co-parent, devices exchange public keys in person via a QR code or shared
        link to derive a symmetric key. That key remains strictly on the two devices.
      </p>
      <p>
        Sync messages are encrypted on-device with ChaCha20-Poly1305. The relay routes opaque
        ciphertext between devices without the ability to decrypt it.
      </p>

      <h3>Data visible to the relay</h3>
      <ul>
        <li>
          A <strong>channel ID</strong>: 16 random bytes generated on your device, unrelated to your
          identity.
        </li>
        <li>
          A <strong>peer ID</strong>: 16 random bytes per device per channel.
        </li>
        <li>
          The <strong>ciphertext</strong>, nonce, and authentication tag.
        </li>
        <li>
          A <strong>public key</strong>, once, while two phones are pairing. This is the one thing
          crossing the relay that isn&rsquo;t encrypted. It is a public key &mdash; it cannot
          decrypt anything on its own &mdash; and it is never stored: undeliverable pairing
          messages are dropped, not queued. Both phones then show an eight-digit code for you to
          compare out loud, which is how you can tell nobody swapped it in transit.
        </li>
        <li>
          A <strong>message timestamp</strong> set by the sending device. Advisory only: the relay
          orders and expires messages by its own clock.
        </li>
      </ul>
      <p>
        No names, email addresses, birth dates, measurements, locations, advertising IDs, or device
        fingerprints are sent to the relay.
      </p>

      <h3>Data stored by the relay</h3>
      <ul>
        <li>
          <strong>Undelivered encrypted messages.</strong> If a receiving phone is offline, the relay
          holds the encrypted payload until delivered and acknowledged, or deletes it automatically
          after <strong>90 days</strong>.
        </li>
        <li>
          <strong>Apple Push Notification service token.</strong> Mapped to channel and peer IDs to
          wake sleeping devices when messages arrive. Push notifications carry no payload. Deleting the
          app disables the token.
        </li>
      </ul>

      <h2>Website</h2>
      <p>
        This site is static and uses no tracking cookies or analytics scripts. Google Fonts are served
        directly.
      </p>
      <p>
        Hosted on Cloudflare Pages. Cloudflare processes standard request metadata (such as IP
        addresses) per{' '}
        <a href="https://www.cloudflare.com/privacypolicy/" rel="noreferrer noopener" target="_blank">
          Cloudflare&rsquo;s privacy policy
        </a>
        .
      </p>

      <h3>Support form</h3>
      <p>
        Submissions sent through the <Link to="/support">support page</Link> are delivered via{' '}
        <a href="https://resend.com/legal/privacy-policy" rel="noreferrer noopener" target="_blank">
          Resend
        </a>{' '}
        and retained only to respond to inquiries. Temporary in-memory rate limiting by IP address
        prevents spam.
      </p>
      <p>Do not include medical details in support requests.</p>

      <h2>Children&rsquo;s privacy</h2>
      <p>
        Bramble records infant care data strictly on caregiver devices with end-to-end encryption in
        transit. We do not collect, read, profile, or sell user data.
      </p>

      <h2>Your rights</h2>
      <p>
        Your data remains in your control on your device. We store no personal profiles. Deleting the
        app from both devices stops channel connections, and queued encrypted messages expire
        automatically.
      </p>

      <h2>Changes</h2>
      <p>
        If this policy changes, the updated date will appear at the top of this page and in app
        release notes.
      </p>

      <h2>Contact</h2>
      <p>
        Questions regarding this policy can be sent via the <Link to="/support">support page</Link>.
      </p>
    </DocPage>
  );
}
